import { ReactNode } from "react";
import { Grid, Typography, Card, Box, Button, Link } from "@mui/material";
import { useTranslation } from "react-i18next";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { TextField, useRecordContext } from "react-admin";
import AnimatedBlock from "../../../components/Common/AnimatedBlock";
import { IfCanAccess } from "@react-admin/ra-rbac";
import Label from "../../../components/Label";
import InfoRow from "../../../components/Common/InfoRow";

const Section = ({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) => (
  <Box mb={3}>
    <Box pb={2}>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
    <AnimatedBlock>
      <Card>
        <Box px={2} py={1}>
          {children}
        </Box>
      </Card>
    </AnimatedBlock>
  </Box>
);

const LogoProjet = ({
  field,
  useBackground,
}: {
  field: string;
  useBackground?: boolean;
}) => {
  const { t }: { t: any } = useTranslation();
  const project = useRecordContext();
  const physicalPayment = project.parameters?.physicalPayment || {};
  const src = physicalPayment[field];
  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: 120,
        height: 48,
        borderRadius: 1,
        border: src ? 0 : 1,
        borderColor: "divider",
        backgroundColor: useBackground ? physicalPayment.backgroundColor : "transparent",
      }}
    >
      {src ? (
        <img src={src} alt={t("Logo preview")} style={{ maxWidth: "80%", maxHeight: "80%" }} />
      ) : (
        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          {t("No logo")}
        </Typography>
      )}
    </Box>
  );
};

const ColourValue = ({ source }: { source: string }) => {
  const project = useRecordContext();
  const value = project.parameters?.physicalPayment?.[source];
  return (
    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
      {value && <Box sx={{ width: 16, height: 16, borderRadius: 0.5, border: 1, borderColor: "divider", backgroundColor: value }} />}
      <TextField source={`parameters.physicalPayment.${source}`} sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400 }} />
    </Box>
  );
};

function ParametersCryoptoPaymentTab() {
  const { t }: { t: any } = useTranslation();
  const project = useRecordContext();
  const coinhouseCounterUrl =
    process.env.NODE_ENV === "production"
      ? "https://counter.coinhouse.com"
      : "https://counter.comptoirdubitcoin.com";
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Section title={t("Interface configuration")} subtitle={t("Name, contact details, logos and colors shown to customers")}>
          <InfoRow label={t("Name")}>
            <TextField source="parameters.physicalPayment.name" />
          </InfoRow>
          <InfoRow label={t("Description")}>
            <TextField source="parameters.physicalPayment.description" />
          </InfoRow>
          <InfoRow label={t("Address")}>
            <TextField source="parameters.physicalPayment.address" />
          </InfoRow>
          <InfoRow label={t("Phone")}>
            <TextField source="parameters.physicalPayment.phone" />
          </InfoRow>
          <InfoRow label={t("Logo")}>
            <LogoProjet field="logo" useBackground={false} />
          </InfoRow>
          <InfoRow label={t("Inverted logo")}>
            <LogoProjet field="logoInverse" useBackground={true} />
          </InfoRow>
          <InfoRow label={t("Background color")}>
            <ColourValue source="backgroundColor" />
          </InfoRow>
          <InfoRow label={t("Font color")}>
            <ColourValue source="fontColor" />
          </InfoRow>
        </Section>
        <Section title={t("Crypto payment configuration")} subtitle={t("Blockchain, conversion and API access of this entity")}>
          <InfoRow label={t("Blockchain")}>
            <TextField source="parameters.blockchain" />
          </InfoRow>
          <InfoRow label={t("Network")}>
            <TextField source="parameters.network" />
          </InfoRow>
          <InfoRow label={t("Auto conversion")}>
            <Label color={project.autoConvert ? "success" : "secondary"}>
              {project.autoConvert ? t("Enabled") : t("Disabled")}
            </Label>
          </InfoRow>
          <InfoRow label={t("API key")}>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              {project?.apiKey ? (
                <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400 }}>
                  {project.apiKey.substring(0, 10)}......
                  {project.apiKey.substring(
                    project.apiKey.length - 10
                  )}
                </Typography>
              ) : (
                <TextField source="apiKey" />
              )}
              <Button
                size="small"
                onClick={() => {
                  const apiKey = project?.apiKey || "";
                  navigator.clipboard.writeText(apiKey);
                }}
                startIcon={<ContentCopyOutlinedIcon />}
              >
                {t("Copy")}
              </Button>
            </Box>
          </InfoRow>
          <InfoRow label={t("Webhook URL")}>
            <TextField source="parameters.webhookUrl" sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400 }} />
          </InfoRow>
        </Section>
        <IfCanAccess resource="projects.userIdCNHS" action="show">
          <Section title={t("Coinhouse configuration")} subtitle={t("The Coinhouse user this entity is linked to")}>
            <InfoRow label={t("Coinhouse user UUID")}>
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <TextField source="userIdCNHS" sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400 }} />
                <Link
                  href={`${coinhouseCounterUrl}/coinhouse-counter/${project.userIdCNHS}`}
                  target="_blank"
                >
                  <Button size="small" variant="text" startIcon={<OpenInNewRoundedIcon />}>
                    {t("Coinhouse Counter")}
                  </Button>
                </Link>
              </Box>
            </InfoRow>
          </Section>
        </IfCanAccess>
      </Grid>
    </Grid>
  );
}

export default ParametersCryoptoPaymentTab;
